import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  PlayCircle, ChevronLeft, ChevronRight, Loader2, CheckCircle2,
  Copy, Check, X as CloseIcon, KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import MemberLayout from "@/components/app/MemberLayout";
import memberApi from "@/lib/memberApi";

export default function CommunityHome() {
  const [data, setData] = useState({ hero: null, swimlanes: [], continue_watching: [], member: null });
  const [loading, setLoading] = useState(true);

  // Post-signup welcome banner (populated by /vod signup redirect via location.state)
  const location = useLocation();
  const navigate = useNavigate();
  const justSignedUp = !!location.state?.justSignedUp;
  const magicLinkUrlFromState = location.state?.magicLinkUrl || null;
  const [showWelcome, setShowWelcome] = useState(justSignedUp);
  const [magicLinkUrl, setMagicLinkUrl] = useState(magicLinkUrlFromState);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await memberApi.get("/community/library");
        setData(data);
      } catch (err) {
        console.error("library load failed", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Fetch magic-link if we came from signup and the state didn't include it (defensive fallback).
  useEffect(() => {
    if (!justSignedUp || magicLinkUrl) return;
    (async () => {
      try {
        const { data } = await memberApi.get("/community/magic-link");
        setMagicLinkUrl(data.magic_link_url);
      } catch (_) { /* silent — banner will still render without the link */ }
    })();
  }, [justSignedUp, magicLinkUrl]);

  // Clear the state entry so refreshing/re-navigating won't re-show the welcome banner.
  useEffect(() => {
    if (justSignedUp) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [justSignedUp, navigate, location.pathname]);

  const handleCopy = async () => {
    if (!magicLinkUrl) return;
    try {
      await navigator.clipboard.writeText(magicLinkUrl);
      setCopied(true);
      toast.success("הלינק הועתק!");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error("לא הצלחנו להעתיק — סמן ידנית.");
    }
  };

  return (
    <MemberLayout>
      {loading ? (
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
        </div>
      ) : (
        <>
          <AnimatePresence>
            {showWelcome && (
              <WelcomeBanner
                memberName={data.member?.name}
                magicLinkUrl={magicLinkUrl}
                copied={copied}
                onCopy={handleCopy}
                onDismiss={() => setShowWelcome(false)}
              />
            )}
          </AnimatePresence>
          <HeroBanner video={data.hero} memberName={data.member?.name} />
          <div className="px-4 sm:px-8 lg:px-12 py-8 space-y-10">
            {data.continue_watching.length > 0 && (
              <Swimlane title="ממשיכים לצפות" videos={data.continue_watching} testId="lane-continue" />
            )}
            {data.swimlanes.map((lane) => (
              <Swimlane
                key={lane.category.id}
                title={lane.category.name}
                videos={lane.videos}
                testId={`lane-${lane.category.id}`}
              />
            ))}
            {data.swimlanes.length === 0 && data.continue_watching.length === 0 && !data.hero && (
              <EmptyLibrary />
            )}
          </div>
        </>
      )}
    </MemberLayout>
  );
}

function WelcomeBanner({ memberName, magicLinkUrl, copied, onCopy, onDismiss }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
      className="relative bg-gradient-to-br from-amber-600/20 via-amber-500/10 to-transparent border-b border-amber-500/30 px-4 sm:px-8 lg:px-12 py-6 sm:py-8"
      data-testid="community-welcome-banner"
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label="סגור"
        className="absolute top-4 left-4 w-8 h-8 rounded-full bg-stone-900/80 hover:bg-stone-800 flex items-center justify-center text-stone-300 transition-colors"
        data-testid="welcome-dismiss"
      >
        <CloseIcon className="w-4 h-4" />
      </button>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-2 text-amber-400 mb-2">
          <CheckCircle2 className="w-5 h-5" />
          <span className="text-sm font-bold tracking-wider uppercase">אתם בפנים</span>
        </div>
        <h2 className="font-[Rubik] font-black text-2xl sm:text-3xl text-stone-50 mb-2 leading-tight">
          {memberName ? `${memberName}, ברוכים הבאים לקהילה!` : "ברוכים הבאים לקהילה!"}
        </h2>
        <p className="text-stone-300 leading-relaxed mb-5 max-w-2xl">
          התחברנו אתכם אוטומטית — אתם יכולים כבר להתחיל לצפות. גם שלחנו לכם למייל לינק כניסה אישי.
          <br className="hidden sm:block" />
          שמרו את הלינק שלמטה כדי להיכנס בקלות בפעם הבאה (גם ממכשיר אחר).
        </p>

        {magicLinkUrl && (
          <div className="bg-stone-950/60 border border-stone-800 rounded-2xl p-3 sm:p-4" data-testid="welcome-magic-link">
            <div className="flex items-center gap-2 text-amber-500 text-xs font-bold uppercase tracking-wider mb-2">
              <KeyRound className="w-4 h-4" />
              הלינק האישי שלך
            </div>
            <div className="flex flex-col sm:flex-row gap-2 items-stretch">
              <input
                type="text"
                readOnly
                value={magicLinkUrl}
                dir="ltr"
                className="flex-1 min-w-0 bg-stone-900 border border-stone-800 rounded-lg px-3 py-2.5 text-xs sm:text-sm text-stone-300 font-mono focus:outline-none focus:border-amber-600 focus:ring-1 focus:ring-amber-600"
                data-testid="welcome-magic-link-input"
                onFocus={(e) => e.currentTarget.select()}
              />
              <button
                type="button"
                onClick={onCopy}
                data-testid="welcome-magic-link-copy"
                className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-bold transition-colors flex-shrink-0 ${
                  copied
                    ? "bg-emerald-600 text-white"
                    : "bg-amber-600 hover:bg-amber-500 text-stone-950"
                }`}
              >
                {copied ? (<><Check className="w-4 h-4" /> הועתק</>) : (<><Copy className="w-4 h-4" /> העתקה</>)}
              </button>
            </div>
            <p className="text-xs text-stone-500 mt-3 leading-relaxed">
              💡 שמרו את הלינק בסימנייה, במייל לעצמכם או באפליקציית הערות. גם אם הסשן שלכם יפוג — הלינק הזה יאפשר לכם להיכנס חזרה בלחיצה.
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function HeroBanner({ video, memberName }) {
  if (!video) return null;
  return (
    <div className="relative w-full h-[60vh] min-h-[420px] overflow-hidden" data-testid="hero-banner">
      <img
        src={video.thumbnail_url}
        alt={video.title}
        className="absolute inset-0 w-full h-full object-cover"
        onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${video.youtube_id}/hqdefault.jpg`; }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/60 to-stone-950/10" />
      <div className="absolute inset-0 bg-gradient-to-l from-stone-950/70 to-transparent" />

      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative z-10 h-full flex flex-col justify-end px-4 sm:px-8 lg:px-12 pb-10 max-w-3xl"
      >
        <div className="text-xs font-bold text-amber-500 tracking-widest uppercase mb-3">
          {memberName ? `ברוכים השבים, ${memberName}` : "ברוכים השבים לאזור הצמיחה"}
        </div>
        <h1 className="font-[Rubik] font-black text-4xl sm:text-5xl lg:text-6xl text-stone-50 mb-4 leading-[1.1] drop-shadow-lg">
          {video.title}
        </h1>
        {video.description && (
          <p className="text-stone-300 text-base sm:text-lg leading-relaxed mb-6 line-clamp-2 max-w-2xl">
            {video.description}
          </p>
        )}
        <div className="flex flex-wrap gap-3">
          <Link
            to={`/community/watch/${video.id}`}
            data-testid="hero-play"
            className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-stone-950 font-bold px-6 py-3 rounded-full transition-colors active:scale-95"
          >
            <PlayCircle className="w-5 h-5" />
            {video.progress_seconds > 0 ? "המשך צפייה" : "צפייה עכשיו"}
          </Link>
        </div>
      </motion.div>
    </div>
  );
}

function Swimlane({ title, videos, testId }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    // RTL: dir=1 moves visually to the right (start), dir=-1 to the left (end)
    el.scrollBy({ left: dir * -320, behavior: "smooth" });
  };

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    // In RTL, scrollLeft is negative or zero → the visible area at start has scrollLeft close to 0
    const atStart = Math.abs(el.scrollLeft) < 10;
    const atEnd = Math.abs(el.scrollLeft) + el.clientWidth >= el.scrollWidth - 10;
    setCanScrollLeft(!atStart);
    setCanScrollRight(!atEnd);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows);
    return () => el.removeEventListener("scroll", updateArrows);
  }, []);

  return (
    <div className="relative" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-[Rubik] font-bold text-xl sm:text-2xl text-stone-100">{title}</h2>
        <div className="hidden sm:flex gap-1">
          <button
            onClick={() => scroll(1)}
            disabled={!canScrollLeft}
            data-testid={`${testId}-right`}
            className="w-9 h-9 rounded-full bg-stone-800 hover:bg-stone-700 disabled:opacity-30 flex items-center justify-center transition-colors"
            aria-label="גלול ימינה"
          >
            <ChevronRight className="w-4 h-4 text-stone-300" />
          </button>
          <button
            onClick={() => scroll(-1)}
            disabled={!canScrollRight}
            data-testid={`${testId}-left`}
            className="w-9 h-9 rounded-full bg-stone-800 hover:bg-stone-700 disabled:opacity-30 flex items-center justify-center transition-colors"
            aria-label="גלול שמאלה"
          >
            <ChevronLeft className="w-4 h-4 text-stone-300" />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 no-scrollbar"
        style={{ scrollbarWidth: "none" }}
      >
        {videos.map((v) => (
          <VideoCard key={v.id} video={v} testId={`${testId}-item-${v.id}`} />
        ))}
      </div>
      <style>{`.no-scrollbar::-webkit-scrollbar{display:none}`}</style>
    </div>
  );
}

function VideoCard({ video, testId }) {
  return (
    <Link
      to={`/community/watch/${video.id}`}
      data-testid={testId}
      className="group flex-shrink-0 w-64 sm:w-72 snap-start rounded-xl overflow-hidden bg-stone-900 border border-stone-800 hover:border-amber-600/50 hover:-translate-y-1 transition-all duration-200 relative"
    >
      <div className="relative aspect-video overflow-hidden bg-stone-800">
        <img
          src={video.thumbnail_url}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${video.youtube_id}/hqdefault.jpg`; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-transparent to-transparent opacity-60" />
        {video.is_completed && (
          <div className="absolute top-2 right-2 bg-amber-600 text-stone-950 rounded-full p-1">
            <CheckCircle2 className="w-4 h-4" />
          </div>
        )}
        {video.duration_seconds > 0 && (
          <div className="absolute bottom-2 left-2 bg-black/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
            {formatDuration(video.duration_seconds)}
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-14 h-14 rounded-full bg-amber-600/90 flex items-center justify-center">
            <PlayCircle className="w-8 h-8 text-stone-950" />
          </div>
        </div>
        {/* Progress bar */}
        {video.progress_percent > 0 && (
          <div className="absolute bottom-0 inset-x-0 h-1 bg-stone-700/70">
            <div className="h-full bg-amber-600" style={{ width: `${video.progress_percent}%` }} />
          </div>
        )}
      </div>
      <div className="p-3">
        <h3 className="font-bold text-sm text-stone-100 line-clamp-2 leading-snug">{video.title}</h3>
      </div>
    </Link>
  );
}

function formatDuration(s) {
  s = Number(s || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

function EmptyLibrary() {
  return (
    <div className="text-center py-24" data-testid="empty-library">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-stone-800 flex items-center justify-center">
        <PlayCircle className="w-8 h-8 text-stone-600" />
      </div>
      <h3 className="font-[Rubik] font-bold text-xl text-stone-300 mb-2">התוכן בדרך אליכם</h3>
      <p className="text-sm text-stone-500">בקרוב יעלו לכאן סרטונים ראשונים לצפייה.</p>
    </div>
  );
}
